export interface QuestionOption {
	value: string;
	label: string;
	icon?: string;
	description?: string;
	badge?: string;
}

// v5 canonical enums (must match era-api catalog.ts — contract §4)
export const GENDER_ENUM = ["female", "male", "non_binary", "other"] as const;
export type GenderEnum = (typeof GENDER_ENUM)[number];

export const FERTILITY_STATUS_ENUM = [
	"trying_to_conceive",
	"preserving_fertility",
	"egg_sperm_donor",
	"surrogacy",
	"currently_pregnant",
	"early_parenting",
] as const;
export type FertilityStatusEnum = (typeof FERTILITY_STATUS_ENUM)[number];

export const FERTILITY_ROLE_ENUM = [
	"primary_patient",
	"partner",
	"support_person",
	"exploring_options",
	"surrogate",
	"intended_parent",
	"parent",
] as const;
export type FertilityRoleEnum = (typeof FERTILITY_ROLE_ENUM)[number];

export const JOURNEY_TYPE_ENUM = [
	"iui",
	"ivf",
	"surrogacy",
	"egg_donation",
	"sperm_donation",
	"egg_freezing",
	"dont_know",
	"other",
] as const;
export type JourneyTypeEnum = (typeof JOURNEY_TYPE_ENUM)[number];

export const DONOR_TYPE_ENUM = ["egg", "sperm"] as const;
export type DonorTypeEnum = (typeof DONOR_TYPE_ENUM)[number];

export interface Question {
	id: string;
	type:
		| "profile-select"
		| "setup-profile"
		| "setup-profile-details"
		| "fertility-status"
		| "fertility-role"
		| "fertility-journey-types"
		| "needs-most"
		| "how-did-you-hear"
		| "personal-info"
		| "gender-relationship"
		| "fertility-path"
		| "interests"
		| "select-provider"
		| "additional-info"
		| "journey-steps"
		| "cycle";
	title: string;
	question: string;
	field: string;
	options?: QuestionOption[];
	fields?: string[];
	placeholder?: string;
}

export interface OnboardingAnswers {
	firstName?: string;
	lastName?: string;
	dayOfBirth?: string;
	monthOfBirth?: string;
	yearOfBirth?: string;
	countryCity?: string;
	languagePreference?: string;
	phoneNumber?: string;
	userType?: string;
	gender?: string;
	fertilityStatus?: string;
	fertilityRole?: string;
	relationshipStatus?: string;
	fertilityJourneyTypes?: string[];
	needsMost?: string[];
	howDidYouHearAboutUs?: string[];
	interestedIn?: string[];
	hasClinicProvider?: boolean | string;
	providerName?: string;
	additionalInformation?: string;
	additionalInfoDisplay?: string;
	// v5 additions
	donorType?: string;
	cycleLastPeriodStart?: string;
	cyclePeriodLengthDays?: string;
	cycleLengthDays?: string;
	cycleRegular?: string;
	cycleNotRelevant?: string;
	journeyCheckedStepIds?: string[];
	[key: string]: string | string[] | boolean | undefined;
}

export interface UserData {
	id?: string;
	userType?: string | null;
	isOnboarded?: boolean;
	firstName?: string | null;
	lastName?: string | null;
	dayOfBirth?: number | null;
	monthOfBirth?: number | null;
	yearOfBirth?: number | null;
	gender?: string | null;
	fertilityStatus?: string | null;
	fertilityRole?: string | null;
	fertilityJourneyTypes?: string[] | null;
	needsMost?: string[] | null;
	howDidYouHearAboutUs?: string[] | null;
	countryCity?: string | null;
	languagePreference?: string | null;
	phoneNumber?: string | null;
	hasClinicProvider?: boolean | string | null;
	providerName?: string | null;
	donorType?: string | null;
	cycleLastPeriodStart?: string | null;
	cyclePeriodLengthDays?: number | null;
	cycleLengthDays?: number | null;
	cycleRegular?: boolean | null;
	cycleNotRelevant?: boolean | null;
	[key: string]: string | number | boolean | string[] | null | undefined;
}

export interface OnboardingSegmentConfig {
	/** Screen the segment starts at (one of the journey-defining screens). */
	startScreen: "status" | "role" | "journey";
	/** Called when the user backs out of the entry screen (cancel). */
	onExit: () => void;
	/** Called once the machine reaches `needs` — the segment (status→cycle) is fully resolved. */
	onSegmentComplete: (answers: OnboardingAnswers) => void;
}

export interface OnboardingWizardProps {
	onComplete: (answers: OnboardingAnswers) => Promise<void>;
	userData: UserData | null;
	/**
	 * Journey step ids already completed/in-progress (`catalogType:stepId`),
	 * reconstructed from UserStepState — which is NOT part of `/api/user`. Seeds
	 * `journeyCheckedStepIds` so resume can tell the steps screen was completed
	 * and Back restores prior selections.
	 */
	initialStepIds?: string[];
	/** When set, runs the wizard as a profile-scoped status→cycle segment. */
	segment?: OnboardingSegmentConfig;
}
